import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";

export function Card({ title, children, footer, className = "" }) {
  return (
    <div className={`p-card ${className}`}>
      <div className="p-card-body">
        {title ? <div className="p-card-title">{title}</div> : null}
        <div className="p-card-content">{children}</div>
        {footer ? <div className="p-card-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

export function Panel({ header, children, className = "" }) {
  return (
    <section className={`p-panel ${className}`}>
      <div className="p-panel-header">{header}</div>
      <div className="p-panel-content">{children}</div>
    </section>
  );
}

const isPromiseLike = (value) =>
  Boolean(value) && (typeof value === "object" || typeof value === "function") && typeof value.then === "function";

export function Button({
  label,
  children,
  className = "",
  type = "button",
  onClick,
  disabled = false,
  loading,
  loadingLabel,
  ...props
}) {
  const [internalLoading, setInternalLoading] = useState(false);
  const isMountedRef = useRef(true);
  const hasControlledLoading = typeof loading === "boolean";
  const isLoading = hasControlledLoading ? Boolean(loading) : internalLoading;
  const isDisabled = Boolean(disabled || isLoading);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  const handleClick = async (event) => {
    if (!onClick || isDisabled) return;
    const result = onClick(event);
    if (!isPromiseLike(result)) return;

    if (!hasControlledLoading && isMountedRef.current) {
      setInternalLoading(true);
    }

    try {
      await result;
    } catch (error) {
      console.error("Button async action failed:", error);
    } finally {
      if (!hasControlledLoading && isMountedRef.current) {
        setInternalLoading(false);
      }
    }
  };

  const buttonContent = isLoading ? (
    <span className="btn-loading">
      <span className="btn-spinner" aria-hidden="true" />
      <span>{loadingLabel || children || label || "Loading..."}</span>
    </span>
  ) : (
    children || label
  );

  return (
    <button
      type={type}
      className={`btn btn-primary ${className}`}
      disabled={isDisabled}
      aria-busy={isLoading}
      onClick={handleClick}
      {...props}
    >
      {buttonContent}
    </button>
  );
}

export function Modal({
  title,
  description,
  icon,
  tone = "primary",
  visible,
  children,
  footer,
  onClose,
  width = "min(90vw, 820px)",
  className = "",
}) {
  if (!visible) return null;

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget && onClose) {
      onClose();
    }
  };

  return (
    <div className="modal-backdrop-custom" onMouseDown={handleBackdropClick}>
      <div
        className={`modal-card modal-tone-${tone} ${className}`}
        style={{ width }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "modal-title" : undefined}
      >
        <div className="modal-header-custom">
          <div className="modal-title-group">
            {icon ? (
              <span className="modal-title-icon" aria-hidden="true">
                <i className={`pi ${icon}`} />
              </span>
            ) : null}
            <div>
              {title ? <h3 id="modal-title">{title}</h3> : null}
              {description ? <p>{description}</p> : null}
            </div>
          </div>
          {onClose ? (
            <button className="modal-close" type="button" onClick={onClose} aria-label="Close">
              <i className="pi pi-times" aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <div className="modal-content-custom">{children}</div>
        {footer ? <div className="modal-footer-custom">{footer}</div> : null}
      </div>
    </div>
  );
}

export function Spinner() {
  return (
    <div className="loading-div">
      <div className="loader" />
    </div>
  );
}

export function Skeleton({ className = "loading-container" }) {
  return <div className={className} />;
}

export function Field({ label, required, children, className = "", error = "" }) {
  const hasError = Boolean(error);
  return (
    <div className={`input-container ${hasError ? "has-error" : ""} ${className}`}>
      <label className="form-label">
        {label}
        {required ? <sup className="validatedField">*</sup> : null}
      </label>
      {children}
      {hasError ? <div className="field-error" role="alert">{error}</div> : null}
    </div>
  );
}

export function Select({ value, onChange, options, labelKey = "label", valueKey = "value", placeholder = "Select", ...props }) {
  const toValue = (option) => {
    if (option === null || option === undefined) return "";
    if (typeof option === "string" || typeof option === "number") return String(option);
    const raw = option?.[valueKey] ?? option?.[labelKey] ?? "";
    return String(raw);
  };

  return (
    <select
      className="form-control form-select"
      value={value ? toValue(value) : ""}
      onChange={(event) => {
        const selected = options.find((option) => toValue(option) === event.target.value) || null;
        onChange(selected);
      }}
      {...props}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={toValue(option)} value={toValue(option)}>
          {option[labelKey]}
        </option>
      ))}
    </select>
  );
}

const parseUsDate = (value) => {
  const match = String(value || "").trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  if (!month || !day || !year || month > 12 || day > 31) return null;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
};

const parseIsoDate = (value) => {
  const match = String(value || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
};

const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const stringValue = String(value).trim();
  if (!stringValue) return null;
  const usDate = parseUsDate(stringValue);
  if (usDate) return usDate;
  const isoDate = parseIsoDate(stringValue);
  if (isoDate) return isoDate;

  const parsed = new Date(stringValue);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

export function DateInput({ value, onChange, disabled, minDate, maxDate, placeholder = "MM/DD/YYYY" }) {
  const selectedDate = useMemo(() => parseDateValue(value), [value]);
  const resolvedMinDate = useMemo(() => parseDateValue(minDate), [minDate]);
  const resolvedMaxDate = useMemo(() => parseDateValue(maxDate), [maxDate]);

  return (
    <div className={`date-picker-shell ${disabled ? "is-disabled" : ""}`}>
      <DatePicker
        selected={selectedDate}
        onChange={(nextDate) => onChange(nextDate || "")}
        disabled={disabled}
        minDate={resolvedMinDate || undefined}
        maxDate={resolvedMaxDate || undefined}
        className="form-control date-picker-control"
        wrapperClassName="date-picker-wrapper"
        calendarClassName="date-picker-calendar"
        popperClassName="date-picker-popper"
        placeholderText={placeholder}
        dateFormat="MM/dd/yyyy"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        scrollableYearDropdown
        yearDropdownItemNumber={90}
        showPopperArrow={false}
        portalId="date-picker-portal"
      />
      <i className="pi pi-calendar date-picker-icon" aria-hidden="true" />
    </div>
  );
}

export function RatingStars({ value }) {
  let rating = 0;

  if (typeof value === "number" && Number.isFinite(value)) {
    rating = value;
  } else if (value !== null && value !== undefined && String(value).trim() !== "") {
    const match = String(value).match(/-?\d+(\.\d+)?/);
    if (match) {
      const parsed = Number(match[0]);
      if (Number.isFinite(parsed)) rating = parsed;
    }
  }

  rating = Math.max(0, Math.min(5, Math.round(rating)));
  return <span className="stars">{Array.from({ length: 5 }, (_, i) => (i < rating ? "★" : "☆")).join("")}</span>;
}

export function DetailBox({ label, value }) {
  return (
    <div className="accountDetailBox">
      <h6>{label}</h6>
      <p>{value || "N/A"}</p>
    </div>
  );
}
